import { loadStripe } from '@stripe/stripe-js';
import { captureMessage } from '@sentry/react';
import config from 'config';
import { ClientBillingInfo } from 'domains/billing/types';
import { fromUnixTime } from 'date-fns';
import { format } from 'domains/dates/format';
import { DATE_SHORT_HUMAN } from 'utils/date';
import { Payment, PaymentIntentRaw } from './types';

 
enum StripeAccount {
  INC = 'acct_1EtnCNCVYcGP97qQ',
  SLP = 'acct_1GpjkLGN8gxeZoYX',
  TPC = 'acct_1Kl37qBo3FXyptOC',
  TNJ = 'acct_1Me37oB8eMFYF0iZ',
  STC = 'acct_1Me3AfHdsQhvtcty',
}

// it contains all stripe accounts publishable keys objects.
// as we have multiple stripe accounts, this is to avoid recreating the `Stripe` objects on every render.
const stripeAccounts = {
  [StripeAccount.INC]: loadStripe(config.get('stripePublishableKeyINC') as string),
  [StripeAccount.SLP]: loadStripe(config.get('stripePublishableKeySLP') as string),
  [StripeAccount.TPC]: loadStripe(config.get('stripePublishableKeyTPC') as string),
  [StripeAccount.TNJ]: loadStripe(config.get('stripePublishableKeyTNJ') as string),
  [StripeAccount.STC]: loadStripe(config.get('stripePublishableKeySTC') as string),
};

export const getStripeFromAccountID = async (stripeAccountID: string) => {
  const stripeAccount = await stripeAccounts[stripeAccountID as StripeAccount];
  if (!stripeAccount) {
    captureMessage(
      `Stripe account "${stripeAccountID}" does not exist. Please take a quick action to handle the new stripe account.`,
    );
  }
  return stripeAccount;
};

export const formatCreditCardExpireDate = (expirationMonth: number, expirationYear: number) => {
  return new Date(expirationYear, expirationMonth - 1).toISOString();
};

export const generateCardNumber = (brand: string, last4: string) => {
  return `${brand.toUpperCase()}···${last4}`;
};

/** Checks whether client is exempt from payments or not */
export const isExemptFromPayment = (info: ClientBillingInfo) => {
  if (info.billingType === 'self-pay') {
    return false;
  }

  if (info.billingType === 'insurance') {
    return info.exemptFromPatientResponsibility;
  }

  return false;
};

/**
 * Combines and transforms invoices and payment intents into a unified receipts array for display.
 */
export function transformReceiptsData({
  invoices,
  paymentIntents,
}: {
  invoices?: Payment[];
  paymentIntents?: PaymentIntentRaw[];
}): Array<{
  type: 'invoice' | 'paymentIntent';
  serviceDate: string;
  amount: number;
  description: string;
  created: number;
  id: string;
  hosted_invoice_url?: string;
  charge?: {
    receipt_url: string;
    metadata?: { serviceDate?: string; description?: string };
    amount?: number;
  };
  paymentIntent?: {
    created: number;
  };
}> {
  return [
    ...(invoices?.map(invoice => ({
      ...invoice,
      type: 'invoice' as const,
      serviceDate: format(fromUnixTime(invoice.created), DATE_SHORT_HUMAN),
      amount: invoice.total,
      description: invoice.description,
      id: invoice.id,
      created: invoice.created,
    })) || []),
    ...(paymentIntents?.map(payment => ({
      ...payment,
      type: 'paymentIntent' as const,
      serviceDate: payment.charge?.metadata?.serviceDate
        ? payment.charge.metadata.serviceDate
        : format(fromUnixTime(payment.paymentIntent.created), DATE_SHORT_HUMAN),
      amount: payment.charge?.amount || 0,
      description: payment.charge?.metadata?.description ?? '-',
      id: payment.paymentIntent.id,
      created: payment.paymentIntent.created,
    })) || []),
  ];
}
